// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides the route component for the account settings page.
 * It displays various account-related components that allow users to manage
 * their personal details and view their audit history.
 */
import { AccountDetailsCard } from '@/account/components/account-details-card';
import { ConnectedApplicationsCard } from '@/account/components/connected-applications-card';
import { NotificationSettingsCard } from '@/account/components/notification-settings-card';
import { PersonAuditCard } from '@/account/components/person-audit-card';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { PageLayout } from '@/components/page-layout';
import { Stack } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

/**
 * Creates the route definition for the account page.
 * This route is accessible at '/_app/account'.
 */
export const Route = createFileRoute('/_app/account')({
  component: AccountRoute,
});

/**
 * The main account settings page component.
 * Displays a vertically stacked layout containing:
 * - Personal details form (name, email)
 * - A link to the notification settings
 * - The applications the user connected, which they can disconnect
 * - Personal audit history
 */
function AccountRoute() {
  const { user } = useCurrentUser();

  return (
    <PageLayout title="User account">
      <PageContent>
        <Stack spacing={3}>
          <AccountDetailsCard user={user} />
          <NotificationSettingsCard />
          <ConnectedApplicationsCard
            organizationName={user.organization.name}
          />
          <PersonAuditCard personKey={user.key} />
        </Stack>
      </PageContent>
    </PageLayout>
  );
}
